<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <!-- * 모달 헤더 -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <Qrcode :size="30" :style="{ paddingTop: '2px' }" />
          </div>
          <h2 class="modal-title">가격표 생성</h2>
        </div>
        <button class="close-button" @click="$emit('close')" aria-label="닫기">
          <Close :style="{ paddingTop: '2px' }" />
        </button>
      </div>

      <!-- * 컨트롤 영역 -->
      <div class="controls-section">
        <div class="button-group">
          <button class="action-button primary" @click="generateQRCodes" :disabled="!hasSelectedItems">
            <Plus :style="{ paddingTop: '2px' }" />가격표 생성
          </button>

          <button class="action-button secondary" @click="printQRCodes" :disabled="!isAllSelected">
            <Printer :style="{ paddingTop: '2px' }" />전체 인쇄
          </button>

          <button class="action-button secondary" @click="printSelectedQRCodes" :disabled="!hasSelectedItems">
            <Printer :style="{ paddingTop: '2px' }" />선택 인쇄
          </button>
        </div>

        <div class="select-all-container">
          <label class="modern-checkbox">
            <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
            <span class="checkmark"></span>
            <span class="checkbox-label">전체 선택</span>
          </label>
        </div>
      </div>

      <!-- * 컨텐츠 영역 -->
      <div class="modal-body">
        <div class="content-container" ref="qrCodesContainer">
          <div class="price-card" v-for="item in groupedInventory" :key="item.name3" :data-name="item.name3"
            :class="{ 'selected': selectedItems[item.name3] }">
            <div class="card-checkbox">
              <label class="modern-checkbox">
                <input type="checkbox" v-model="selectedItems[item.name3]" @change="updateSelectAll">
                <span class="checkmark small"></span>
              </label>
            </div>

            <div class="card-content">
              <div class="product-info">
                <h3 class="product-title">{{ item.name3 }}</h3>
                <p class="product-option" v-if="getOptionDisplay(item)">
                  {{ getOptionDisplay(item) }}
                </p>
                <div class="product-price">
                  {{ formatPrice(item.price?.price || item.price) }}원
                </div>
              </div>

              <div class="qr-code-container" v-if="qrCodeUrls[item.name3]">
                <img :src="qrCodeUrls[item.name3]" class="qr-code" alt="QR 코드" />
              </div>

              <div class="product-visual">
                <div class="product-image-container">
                  <img class="product-image" :src="item.imageUrl" @error="handleImageError" alt="상품 이미지" />
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';

import Plus from 'vue-material-design-icons/Plus.vue';
import Printer from 'vue-material-design-icons/Printer.vue';
import Close from 'vue-material-design-icons/Close.vue';
import Qrcode from 'vue-material-design-icons/Qrcode.vue';

export default {
  name: 'QRprintModal',

  props: {
    inventory: {
      type: Array,
      required: true
    }
  },

  components: {
    Plus,
    Printer,
    Close,
    Qrcode
  },

  data() {
    return {
      qrCodesGenerated: false,
      selectedItems: {},
      selectAll: false,
      qrCodeUrls: {}
    };
  },

  async created() {
    for (const item of this.inventory) {
      item.imageUrl = await this.getImageUrl(item);
    }
    document.addEventListener('keydown', this.handleEscKey);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscKey);
  },

  computed: {
    formatPrice() {
      return (price) => {
        if (!price) return '0';

        let priceValue = price;
        if (typeof price === 'object' && price !== null) {
          priceValue = price.price || 0;
        }

        if (typeof priceValue === 'string') {
          priceValue = priceValue.replace(/,/g, '');
        }

        const numPrice = Number(priceValue);

        if (isNaN(numPrice)) return '0';

        return numPrice.toLocaleString('ko-KR');
      };
    },
    groupedInventory() {
      const groups = {};

      this.inventory.forEach(item => {
        const name = item.name3;
        if (!groups[name]) {
          groups[name] = {
            ...item,
            colors: new Set(),
            sizes: new Set(),
            bcodes: new Set()
          };
        }

        if (item.options?.color?.length > 0) {
          item.options.color.forEach(c => groups[name].colors.add(c));
        } else if (item.color) {
          groups[name].colors.add(item.color);
        }

        if (item.options?.size?.length > 0) {
          item.options.size.forEach(s => groups[name].sizes.add(s));
        } else if (item.size) {
          groups[name].sizes.add(item.size);
        }

        if (item.options?.bcode?.length > 0) {
          item.options.bcode.forEach(b => groups[name].bcodes.add(b));
        } else if (item.bcode) {
          groups[name].bcodes.add(item.bcode);
        }
      });

      return Object.values(groups).map(group => {
        const result = {
          ...group,
          colors: Array.from(group.colors),
          sizes: Array.from(group.sizes),
          bcodes: Array.from(group.bcodes)
        };
        return result;
      });
    },
    hasSelectedItems() {
      return Object.values(this.selectedItems).some(value => value);
    },
    isAllSelected() {
      const totalItems = this.groupedInventory.length;
      const selectedCount = Object.values(this.selectedItems).filter(value => value).length;
      return totalItems === selectedCount && totalItems > 0;
    }
  },

  methods: {
    handleOverlayClick(event) {
      if (event.target === event.currentTarget) {
        this.$emit('close');
      }
    },

    getOptionDisplay(item) {
      if (!item) return '';

      const colors = item.colors || [];
      const sizes = item.sizes || [];

      const colorText = colors.length > 0 ? colors.join(', ') : '';
      const sizeText = sizes.length > 0 ? sizes.join(', ') : '';

      if (colorText && sizeText) {
        return `${colorText} / ${sizeText}`;
      } else if (colorText) {
        return colorText;
      } else if (sizeText) {
        return sizeText;
      }
      return '';
    },

    async getImageUrl(item) {
      try {
        const s3BaseUrl = process.env.VUE_APP_S3_BUCKET;

        if (typeof item === 'string') {
          return `${s3BaseUrl}/Product-image/${item}.jpg`;
        }

        if (!item) {
          return '/icon.png';
        }

        if (item.code) {
          const codeUrl = `${s3BaseUrl}/Product-image/${item.code}.jpg`;
          return codeUrl;
        }

        if (item.num && item.num !== item.code) {
          const numUrl = `${s3BaseUrl}/Product-image/${item.num}.jpg`;
          return numUrl;
        }

        return '/icon.png';

      } catch (error) {
        console.error('이미지 URL 생성 중 오류 발생:', error);
        return '/icon.png';
      }
    },

    handleImageError(e) {
      const target = e.target;
      if (target && target.parentElement) {
        target.parentElement.style.display = 'none';
      }
    },

    async generateQRCodes() {
      try {
        const selectedItems = this.groupedInventory.filter(item => {
          return this.selectedItems[item.name3];
        });

        let hasGeneratedQRCodes = false;
        let existingBarcodeCount = 0;
        let generatedBarcodeCount = 0;

        await Promise.all(selectedItems.map(async (item) => {
          const bcode = item.bcodes?.length > 0 ? item.bcodes[0] : item.bcode;

          if (bcode) {
            // 기존 바코드가 있는 경우
            try {
              const qrCodeUrl = await QRCode.toDataURL(bcode, {
                errorCorrectionLevel: 'M',
                type: 'image/png',
                quality: 0.92,
                margin: 1,
                color: {
                  dark: '#000000',
                  light: '#FFFFFF'
                }
              });

              this.qrCodeUrls[item.name3] = qrCodeUrl;
              hasGeneratedQRCodes = true;
              existingBarcodeCount++;
            } catch (error) {
              console.error('QR 코드 생성 오류:', error);
            }
          } else {
            // 바코드가 없는 경우 임의 바코드 생성
            try {
              const generatedBarcode = this.generateEAN13Barcode(item);

              const qrCodeUrl = await QRCode.toDataURL(generatedBarcode, {
                errorCorrectionLevel: 'M',
                type: 'image/png',
                quality: 0.92,
                margin: 1,
                color: {
                  dark: '#000000',
                  light: '#FFFFFF'
                }
              });

              this.qrCodeUrls[item.name3] = qrCodeUrl;
              hasGeneratedQRCodes = true;
              generatedBarcodeCount++;

            } catch (error) {
              console.error('임시 바코드 QR 코드 생성 오류:', error);
            }
          }
        }));

        this.qrCodesGenerated = hasGeneratedQRCodes;

        if (hasGeneratedQRCodes) {
          let message = '';
          if (existingBarcodeCount > 0 && generatedBarcodeCount > 0) {
            message = `기존 바코드 ${existingBarcodeCount}개, 임시 바코드 ${generatedBarcodeCount}개가 생성되었습니다.`;
          } else if (existingBarcodeCount > 0) {
            message = `기존 바코드 ${existingBarcodeCount}개가 생성되었습니다.`;
          } else if (generatedBarcodeCount > 0) {
            message = `임시 바코드 ${generatedBarcodeCount}개가 생성되었습니다.`;
          }

          this.$swal.fire({
            title: '성공',
            html: `
              <p>바코드 QR 코드가 생성되었습니다.</p>
              <p><strong>${message}</strong></p>
              ${generatedBarcodeCount > 0 ? '<p class="text-warning">※ 임시 바코드는 실제 등록이 필요합니다.</p>' : ''}
            `,
            icon: 'success',
            confirmButtonText: '확인'
          });
        } else {
          this.$swal.fire({
            title: '알림',
            text: '선택된 품목이 없거나 QR 코드 생성에 실패했습니다.',
            icon: 'warning',
            confirmButtonText: '확인'
          });
        }
      } catch (error) {
        console.error('QR 코드 생성 중 오류:', error);
        this.$swal.fire({
          title: '오류',
          text: 'QR 코드 생성 중 오류가 발생했습니다.',
          icon: 'error',
          confirmButtonText: '확인'
        });
      }
    },

    // EAN-13 바코드 생성 메서드 추가
    generateEAN13Barcode(item) {
      console.log('item', item);
      // 1. 국가 코드 (한국: 880)
      const countryCode = '880';

      // 2. 제조업체 코드 (companykey를 4자리로 패딩)
      const companykey = this.$globalState.company_key || 0;
      const manufacturerCode = companykey.toString().padStart(4, '0');
      console.log('manufacturerCode', manufacturerCode);

      // 3. 상품 코드 (품목 번호 기반으로 5자리)
      let productCode;
      if (item.num) {
        // 품목 번호가 있으면 이를 기반으로 5자리 생성
        productCode = item.num.toString().padStart(5, '0').slice(-5);
      } else {
        // 품목 번호가 없으면 품목명 해시 기반으로 생성
        const hash = this.simpleHash(item.name3 || 'unknown');
        productCode = (hash % 100000).toString().padStart(5, '0');
      }

      // 4. 체크 디지트 계산을 위한 12자리
      const partial = countryCode + manufacturerCode + productCode;

      // 5. EAN-13 체크 디지트 계산
      const checkDigit = this.calculateEAN13CheckDigit(partial);

      // 6. 완성된 13자리 바코드
      return partial + checkDigit;
    },

    // 간단한 해시 함수 (문자열을 숫자로 변환)
    simpleHash(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 32비트 정수로 변환
      }
      return Math.abs(hash);
    },

    // EAN-13 체크 디지트 계산
    calculateEAN13CheckDigit(barcode12) {
      const digits = barcode12.split('').map(Number);
      let sum = 0;

      for (let i = 0; i < 12; i++) {
        if (i % 2 === 0) {
          sum += digits[i];      // 홀수 자리 (1, 3, 5, ...)
        } else {
          sum += digits[i] * 3;  // 짝수 자리 (2, 4, 6, ...) * 3
        }
      }

      const remainder = sum % 10;
      return remainder === 0 ? 0 : 10 - remainder;
    },

    printQRCodes() {
      const allContent = this.$refs.qrCodesContainer.innerHTML;
      this.printContent(allContent);
    },

    printSelectedQRCodes() {
      const selectedContent = this.groupedInventory
        .filter(item => this.selectedItems[item.name3])
        .map(item => {
          const element = this.$refs.qrCodesContainer.querySelector(`[data-name="${item.name3}"]`);
          return element ? element.outerHTML : '';
        })
        .join('');

      this.printContent(selectedContent);
    },

    printContent(content) {
      const printStyles = `
        @page {
          size: A4;
          margin: 5mm;
        }
        
        body {
          margin: 0;
          padding: 0;
          width: 200mm;
        }
        
        .content-container {
          display: grid;
          grid-template-columns: repeat(2, 98mm);
          gap: 5mm;
          padding: 1mm;
          justify-content: center;
        }
        
        .price-card {
          width: 80mm;
          height: 45mm;
          border: 2px solid #FF6B00;
          border-radius: 4px;
          padding: 10px;
          position: relative;
          background: white;
          box-sizing: border-box;
          page-break-inside: avoid;
        }
        
        .card-content {
          width: 100%;
          height: 100%;
          position: relative;
          display: flex;
          justify-content: space-between;
        }
        
        .product-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-bottom: 10px;
          padding-right: 25mm;
        }
        
        .product-title {
          font-size: 16px;
          font-weight: bold;
          margin: 0 0 8px 0;
          line-height: 1.2;
        }
        
        .product-option {
          font-size: 14px;
          color: #FF6B00;
          margin: 8px 0;
          margin-bottom: 10px;
          font-weight: 500;
        }
        
        .product-price {
          font-size: 16px;
          font-weight: bold;
          color: #333;
        }
        
        .product-visual {
          position: absolute;
          top: 0;
          right: 0;
          width: 20mm;
          height: 100%;
        }
        
        .qr-code-container {
          position: absolute;
          top: 0;
          right: 0;
          width: 18mm;
          height: 18mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .qr-code {
          width: 18mm !important;
          height: 18mm !important;
          object-fit: contain;
        }
        
        .product-image-container {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 18mm;
          height: 18mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .product-image {
          max-width: 18mm;
          max-height: 18mm;
          object-fit: contain;
        }
        
        /* 이미지가 없는 경우 숨기기 */
        .product-image-container[style*="display: none"] {
          display: none !important;
        }
        
        .card-checkbox {
          display: none !important;
        }
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const html = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>가격표 인쇄</title>
              <meta charset="utf-8">
              <style>${printStyles}</style>
            </head>
            <body>
              <div class="content-container">${content}</div>
            </body>
          </html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();

        printWindow.onload = function () {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        this.$swal.fire({
          title: '오류',
          text: '팝업이 차단되어 있습니다. 팝업 차단을 해제해주세요.',
          icon: 'error',
          confirmButtonText: '확인'
        });
      }
    },

    toggleSelectAll() {
      this.groupedInventory.forEach(item => {
        this.selectedItems[item.name3] = this.selectAll;
      });
      this.selectedItems = { ...this.selectedItems };
    },

    updateSelectAll() {
      const totalItems = this.groupedInventory.length;
      const selectedCount = Object.values(this.selectedItems).filter(value => value).length;
      this.selectAll = totalItems === selectedCount && totalItems > 0;
    },

    handleEscKey(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    }
  },
};
</script>

<style scoped>
/* 모달 오버레이 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* 모달 컨테이너 */
.modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 모달 헤더 */
.modal-header {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 20px 20px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 35px;
  height: 35px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.close-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 10px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

/* 컨트롤 섹션 */
.controls-section {
  background: #f8fafc;
  padding: 15px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.button-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 15px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: -0.2px;
}

.action-button.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  box-shadow: 0 4px 14px 0 rgba(59, 130, 246, 0.4);
}

.action-button.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px 0 rgba(59, 130, 246, 0.5);
}

.action-button.secondary {
  background: white;
  color: #475569;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
}

.action-button.secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* 체크박스 컨테이너 */
.select-all-container {
  display: flex;
  align-items: center;
}

/* 현대적인 체크박스 */
.modern-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 10px;
}

.modern-checkbox input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  position: relative;
  transition: all 0.2s ease;
}

.checkmark.small {
  width: 16px;
  height: 16px;
}

.modern-checkbox input[type="checkbox"]:checked+.checkmark {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-color: #3b82f6;
}

.modern-checkbox input[type="checkbox"]:checked+.checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.modern-checkbox input[type="checkbox"]:checked+.checkmark.small::after {
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
}

.checkbox-label {
  font-size: 15px;
  font-weight: 500;
  color: #374151;
}

/* 모달 바디 */
.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.content-container {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  align-content: start;
}

/* 커스텀 스크롤바 */
.content-container::-webkit-scrollbar {
  width: 8px;
}

.content-container::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.content-container::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  border-radius: 4px;
}

.content-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
}

/* 가격 카드 */
.price-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 15px;
  position: relative;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.price-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
  border-color: #ff6b35;
}

.price-card.selected {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  box-shadow: 0 8px 25px -3px rgba(59, 130, 246, 0.2);
}

.card-checkbox {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 2;
}

.card-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
}

.product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.product-option {
  font-size: 14px;
  color: #ff6b35;
  font-weight: 600;
  background: rgba(255, 107, 53, 0.1);
  padding: 6px 12px;
  border-radius: 8px;
  display: inline-block;
  margin: 0;
}

.product-price {
  font-size: 20px;
  font-weight: 800;
  color: #1f2937;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.product-visual {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.product-image-container {
  width: 80px;
  height: 80px;
  border: 2px solid #f1f5f9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8fafc;
}

.product-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.qr-code-container {
  width: 75px;
  height: 75px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 4px;
}

.qr-code {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain;
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .modal-container {
    max-height: 95vh;
    border-radius: 16px;
  }

  .modal-header {
    padding: 20px 24px;
    border-radius: 16px 16px 0 0;
  }

  .modal-title {
    font-size: 20px;
  }

  .controls-section {
    padding: 20px 24px;
    flex-direction: column;
    align-items: stretch;
  }

  .button-group {
    justify-content: center;
  }

  .content-container {
    padding: 24px;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .price-card {
    padding: 20px;
  }

  .card-content {
    flex-direction: column;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: 16px 20px;
  }

  .modal-title {
    font-size: 18px;
  }

  .controls-section {
    padding: 16px 20px;
  }

  .action-button {
    padding: 10px 16px;
    font-size: 13px;
  }

  .content-container {
    padding: 20px;
  }

  .price-card {
    padding: 16px;
  }
}
</style>